// lib/services/compute/sticker_cpu_pool.ts

import TypedSlotPool, { PostReleasePolicy } from "../lib/services/compute/typed_slot_pool";
import StickerCpuSlot from "./sticker_cpu_slot";
import { SlotState } from "../lib/services/compute/base_slot"
const SLOT_COUNT = 3;

/**
 * Three-slot pool for sticker readback. Each slot holds a MAP_READ
 * buffer that receives a copy of the sticker detection results from the
 * GPU compute pipeline.
 *
 * Producer: StickerCompute
 *   - acquires an Idle slot
 *   - encodes a copyBufferToBuffer from the gpu slot's sticker_buffer
 *   - after submit, kicks off mapAsync
 *   - on map completion, marks Ready
 *
 * Consumer: Video SceneObject / ScanController
 *   - peeks the latest Ready slot
 *   - reads mapped_data to check for detected sticker codes
 *   - calls unmap_and_recycle when done
 */
export default class StickerCpuPool {
  private pool: TypedSlotPool<StickerCpuSlot>;
  private slots: StickerCpuSlot[] = [];
  private initialized: boolean = false;
  private sequence_counter: number = 0;

  constructor() {
    this.pool = new TypedSlotPool<StickerCpuSlot>(PostReleasePolicy.KeepReady);
  }

  initialize(device: GPUDevice, key: string, sticker_buffer_size: number, blob_buffer_size: number): boolean {
    if (this.initialized) return true;

    this.slots = [];
    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot = new StickerCpuSlot();
      slot.allocate_buffer(device, key, i, sticker_buffer_size,blob_buffer_size);
      this.slots.push(slot);
    }

    this.pool.initialize(this.slots);
    this.initialized = true;
    return true;
  }

  shutdown(): void {
    for (const slot of this.slots) {
      slot.destroy_buffers();
    }
    this.pool.shutdown();
    this.slots = [];
    this.sequence_counter = 0;
    this.initialized = false;
  }

  clear():void
  {
    // Return each safe slot (Idle/Recording/Ready/Leased) to a reusable Idle
    // state with fresh pool ordering metadata. A slot with an in-flight/pending
    // map (state === Pending) is NOT unmapped or marked Idle here: request_clear
    // only sets its deferred-cancellation flag and leaves it Pending (hence
    // unacquirable). Its begin_map() continuation performs the unmap + Idle
    // transition, and finalize_cancelled_slot() returns it to the free list.
    // This avoids unmapping a buffer out from under an awaiting mapAsync.
    for (const slot of this.slots)
    {
        const deferred = slot.request_clear();
        if (!deferred) {
          this.pool.mark_slot_never_used(slot);
        }
    }

  }

  /**
   * Return a slot whose begin_map() was cancelled (deferred clear or stale
   * generation) to the idle free-list — but ONLY if the cancelled operation
   * still owns the slot. If a newer begin_record advanced the generation, the
   * newer operation owns the slot and it is left untouched. begin_map() has
   * already unmapped and reset the owned slot to Idle before this is called.
   */
  finalize_cancelled_slot(slot: StickerCpuSlot, token: number): void {
    if (!slot.owns_generation(token)) return;
    this.pool.mark_slot_never_used(slot);
  }

  is_initialized(): boolean { return this.initialized; }

  // ---- Producer-side API ------------------------------------------------

  acquire_idle_slot(): StickerCpuSlot | null {
    return this.pool.acquire_idle_slot();
  }

  /**
   * After the mapAsync resolves successfully, publish the slot so
   * consumers can read the sticker data.
   */
  publish_slot(slot: StickerCpuSlot, timestamp_sec: number): void {
    this.sequence_counter += 1;
    this.pool.publish_slot(slot, this.sequence_counter, timestamp_sec);
  }

  cancel_slot(slot: StickerCpuSlot): void
  {
     slot.cancel_and_recycle();
  }

  // ---- Consumer-side API ------------------------------------------------

  peek_latest_ready_slot(): StickerCpuSlot | null {
    return this.pool.peek_latest_ready_slot();
  }

  acquire_latest_ready_slot(): StickerCpuSlot | null {
    return this.pool.acquire_latest_ready_slot();
  }

  release_latest_ready_slot(): void {
    this.pool.release_leased_slot();
  }

  // ---- Introspection ----------------------------------------------------
  get_idle_count(): number {
  let count = 0;
  for (const slot of this.slots) {
    if (slot.state === SlotState.Idle) count++;
  }
  return count;
}
  get_latest_published_sequence(): number { return this.pool.getLatestPublishedSequence(); }
  has_published_slot(): boolean { return this.pool.getLatestPublishedSequence() > 0; }

  /** Logging only: per-state slot counts + latest published sequence. */
  census(): {
    idle: number;
    recording: number;
    pending: number;
    ready: number;
    leased: number;
    latest_seq: number;
  } {
    const c = {
      idle: 0,
      recording: 0,
      pending: 0,
      ready: 0,
      leased: 0,
      latest_seq: this.pool.getLatestPublishedSequence(),
    };
    for (const slot of this.slots) {
      switch (slot.state) {
        case SlotState.Idle: c.idle++; break;
        case SlotState.Recording: c.recording++; break;
        case SlotState.Pending: c.pending++; break;
        case SlotState.Ready: c.ready++; break;
        case SlotState.Leased: c.leased++; break;
      }
    }
    return c;
  }
}